import React from "react";
import TextStyles from "../../../../../../components/Elements/TextStyles/TextStyles";
import { ReactComponent as Back } from "../../../../../../assets/svgs/back-button.svg";
import SupplychainCard from "../../common/SupplychainCard";
import { ReactComponent as DashboardIcon } from "../../../../../../assets/svgs/dashboardso1.svg";
import { ReactComponent as ModeOFDashboardIcon } from "../../../../../../assets/svgs/modeOFDashboardIcon.svg";
import { ReactComponent as DashboardsocialIcon1 } from "../../../../../../assets/svgs/DashboardsocialIcon1.svg";
import { ReactComponent as DashboardsocialIcon2 } from "../../../../../../assets/svgs/DashboardsocialIcon2.svg";
import { ReactComponent as DashboardIcon2 } from "../../../../../../assets/svgs/dashboardso2.svg";
import { ReactComponent as DashboardIcon3 } from "../../../../../../assets/svgs/dashboardso3.svg";
import { ReactComponent as DashboardIcon401 } from "../../../../../../assets/svgs/dashboardso401.svg";
import { ReactComponent as DashboardIcon501 } from "../../../../../../assets/svgs/dashboardso501.svg";
import { ReactComponent as DashboardIcon601 } from "../../../../../../assets/svgs/dashboardso601.svg";
import { ReactComponent as DashboardIcon701 } from "../../../../../../assets/svgs/dashboardso701.svg";
import SimplePieChart from "../../common/SimplePieChart";
import ProgressChartSupply from "../../common/ProgressChartSupply";
import SupplierCalEmission from "./SupplierCalEmission";
import VerticalBarChart from "../../common/VerticalBarChart";
import Tier1SupplierTable from "./Tier1SupplierTable";
import ReductionInFootpintEmission from "./ReductionInFootpintEmission";
import ChartCard from "../../common/ChartCard";
import { THEME_COLORS } from "../../../../../../color";
import TimeFilterBar from "../../TimeFilterBar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import PermissionForm from "../../../../../Auth/PermissionForm";
import { getApi } from "../../../../../../api/api_instance";
import { API_URLS } from "../../../../../../api/apiUrls";

const SupplyChain = () => {
  const navigate = useNavigate();
  const [showPermission, setPermission] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeDashboard, setActiveDashboard] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true" ? "live" : "dummy";
  });
  const [loading, setLoading] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true";
  });
  const { user, selectedGroupMember } = useSelector((state) => state.auth);
  const [yearFilter, setYearFilter] = useState(() =>
    user?.reportingYearType === "calendar" ? "2026" : "2026-2027",
  );
  const isPermissionCompleted = user?.isCompleted;

  const selectedMemberId =
    selectedGroupMember === "string"
      ? selectedGroupMember
      : selectedGroupMember?.clientId ||
        selectedGroupMember?._id ||
        selectedGroupMember?.client_id?._id ||
        selectedGroupMember?.client_id;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (activeDashboard !== "live") {
        setLoading(false);
        setDashboardData(null);
        return;
      }

      try {
        setLoading(true);
        let apiUrl;

        if (selectedMemberId) {
          apiUrl = API_URLS.GET_GROUP_MEMBER_DASHBOARD(
            "supply_chain",
            selectedMemberId,
          );
        } else {
          apiUrl = API_URLS.GET_SDG_DASHBOARD("supply_chain");
        }

        const queryParams = new URLSearchParams();
        if (yearFilter) queryParams.append("year", yearFilter);
        const queryString = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        apiUrl += queryString;

        console.log("Supply Chain Dashboard API Hit:", apiUrl);
        const res = await getApi({
          apiUrl: apiUrl,
        });
        console.log("Supply Chain Response:", res);
        if (res?.data) {
          const data = res.data.dashboard ? res.data.dashboard : res.data;
          setDashboardData(data);
        }
      } catch (error) {
        console.error("Error fetching Supply Chain dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [selectedMemberId, activeDashboard, yearFilter]);

  useEffect(() => {
    if (!isPermissionCompleted) {
      setPermission(true);
    }
  }, [isPermissionCompleted]);

  const getComponent = (id) =>
    activeDashboard === "live"
      ? dashboardData?.components?.find((c) => c.componentId === id)
      : null;

  // Components mapping:
  const sustainableComp = getComponent("sc_sustainable_supplier");
  const upgradedComp = getComponent("sc_suppliers_upgraded");
  const investScComp = getComponent("sc_investment_sustainable_sc");
  const pctRevComp = getComponent("sc_invested_pct_revenue");
  const investTrainComp = getComponent("sc_investment_training");
  const sessionsComp = getComponent("sc_training_sessions");
  const numSuppliersComp = getComponent("sc_number_of_suppliers");
  const upstreamDownstreamComp = getComponent("sc_upstream_downstream");
  const sustainablePctComp = getComponent("sc_sustainable_pct");
  const msmeProcurementComp = getComponent("sc_msme_procurement");
  const femaleMaleComp = getComponent("sc_female_male_suppliers");
  const transportModeComp = getComponent("sc_transport_mode");

  // Dynamic values:
  const upgradedVal =
    activeDashboard === "live"
      ? sustainableComp?.data?.value !== undefined
        ? String(sustainableComp.data.value)
        : "0"
      : "50";
  const upgradedDesc =
    activeDashboard === "live"
      ? `Total ${upgradedComp?.data?.value ?? 0}% suppliers are upgraded towards sustainability`
      : "Total 10% suppliers are upgraded towards sustainability ";

  const investScVal =
    activeDashboard === "live"
      ? investScComp?.data?.value !== undefined
        ? `₹ ${(investScComp.data.value / 10000000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Cr`
        : "₹ 0 Cr"
      : "₹ 7.23 Cr";
  const investScDesc =
    activeDashboard === "live"
      ? `Invested ${pctRevComp?.data?.percentage ?? 0}% from the total revenue`
      : "Invested 2% from the total revenue";

  const investTrainVal =
    activeDashboard === "live"
      ? investTrainComp?.data?.value !== undefined
        ? `₹ ${investTrainComp.data.value.toLocaleString()}`
        : "₹ 0"
      : "50L";
  const investTrainDesc =
    activeDashboard === "live"
      ? `Total ${sessionsComp?.data?.value ?? 0} sessions conducted on training & awareness`
      : "Total 56 sessions conducted on training & awareness";

  // Pie chart data:
  const numSuppliersData =
    activeDashboard === "live"
      ? numSuppliersComp?.data?.values || [0, 0]
      : [420, 80];
  const numSuppliersLabels =
    activeDashboard === "live"
      ? numSuppliersComp?.data?.labels || [
          "Local Suppliers",
          "International Suppliers",
        ]
      : ["Local Supplier", "International Supplier"];

  const upstreamDownstreamData =
    activeDashboard === "live"
      ? upstreamDownstreamComp?.data?.values || [0, 0]
      : [350, 150];
  const upstreamDownstreamLabels =
    activeDashboard === "live"
      ? upstreamDownstreamComp?.data?.labels || ["Upstream", "Downstream"]
      : ["Upstream", "Downstream"];

  // Progress chart data:
  const sustainablePctTotal =
    activeDashboard === "live"
      ? sustainablePctComp?.data?.total !== undefined &&
        sustainablePctComp?.data?.total !== null
        ? sustainablePctComp.data.total
        : 0
      : 500;
  const sustainablePctMain =
    activeDashboard === "live"
      ? sustainablePctComp?.data?.values?.[0] !== undefined &&
        sustainablePctComp?.data?.values?.[0] !== null
        ? sustainablePctComp.data.values[0]
        : sustainablePctComp?.data?.percentage !== undefined &&
            sustainablePctComp?.data?.percentage !== null
          ? sustainablePctComp.data.percentage
          : 0
      : 50;

  const msmeProcurementTotal =
    activeDashboard === "live"
      ? msmeProcurementComp?.data?.totalSuppliers !== undefined &&
        msmeProcurementComp?.data?.totalSuppliers !== null
        ? msmeProcurementComp.data.totalSuppliers
        : 0
      : 500;
  const msmeProcurementMain =
    activeDashboard === "live"
      ? msmeProcurementComp?.data?.msmeProcurement !== undefined &&
        msmeProcurementComp?.data?.msmeProcurement !== null
        ? msmeProcurementComp.data.msmeProcurement
        : msmeProcurementComp?.data?.percentage !== undefined &&
            msmeProcurementComp?.data?.percentage !== null
          ? msmeProcurementComp.data.percentage
          : 0
      : 120;

  const femaleMaleTotal =
    activeDashboard === "live"
      ? femaleMaleComp?.data?.total !== undefined &&
        femaleMaleComp?.data?.total !== null
        ? femaleMaleComp.data.total
        : 0
      : 500;
  const femaleMaleMain =
    activeDashboard === "live"
      ? femaleMaleComp?.data?.values?.[0] !== undefined &&
        femaleMaleComp?.data?.values?.[0] !== null
        ? femaleMaleComp.data.values[0]
        : femaleMaleComp?.data?.percentages?.[0] !== undefined &&
            femaleMaleComp?.data?.percentages?.[0] !== null
          ? femaleMaleComp.data.percentages[0]
          : 0
      : 40;

  const transportModeCategories =
    activeDashboard === "live" && transportModeComp?.data?.datasets
      ? transportModeComp.data.datasets.map((d) => d.label)
      : ["Road", "Rail", "Water", "Air"];

  const transportModeData =
    activeDashboard === "live" && transportModeComp?.data?.datasets
      ? transportModeComp.data.datasets.map((d) => d.percentage ?? 0)
      : [25, 45, 20, 10];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#F5F5F5]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#458276]"></div>
      </div>
    );
  }

  return (
    <div className="md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6">
      {!isPermissionCompleted && showPermission && (
        <PermissionForm permissionConClose={() => setPermission(false)} />
      )}
      <TimeFilterBar
        title=" Supply Chain"
        showBreadcrumb={true}
        breadcrumbItems={["Home", " Supply Chain"]}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
        activeDashboard={activeDashboard}
        onDashboardChange={setActiveDashboard}
        onYearChange={setYearFilter}
        selectedYearValue={yearFilter}
      />

      <div
        className={` ${
          !isPermissionCompleted ? "blur-sm opacity-20 pointer-events-none" : ""
        }`}
      >
        {/* Carbon Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
          <SupplychainCard
            icon={<DashboardIcon />}
            title="Upgraded sustainable supplier"
            value={upgradedVal}
            width="100%"
            description={upgradedDesc}
            descriptionBgColor={THEME_COLORS.green}
          />
          <SupplychainCard
            icon={<DashboardIcon2 />}
            title="Investment in Sustainable Supply Chain"
            value={investScVal}
            width="100%"
            description={investScDesc}
            descriptionBgColor={THEME_COLORS.green}
          />
          <SupplychainCard
            icon={<DashboardIcon3 />}
            title="Investment in Training & Awareness"
            value={investTrainVal}
            width="100%"
            description={investTrainDesc}
            descriptionBgColor={THEME_COLORS.green}
          />
        </div>

        {/* Pie Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
          {[
            {
              title: "Number of Supplier",
              data: numSuppliersData,
              labels: numSuppliersLabels,
              colors: [THEME_COLORS.green, THEME_COLORS.yellow],
              icons: <DashboardsocialIcon1 />,
            },
            {
              title: "Upstream and Downstream",
              data: upstreamDownstreamData,
              labels: upstreamDownstreamLabels,
              colors: [THEME_COLORS.green, THEME_COLORS.yellow],
              icons: <DashboardsocialIcon2 />,
            },
          ].map((chart, index) => (
            <div
              key={index}
              className="w-full max-w-md mx-auto lg:max-w-none border border-[#3BA2B959] rounded-lg shadow-md pt-5 bg-white"
            >
              <div className="flex items-center px-4 mb-2">
                <div className="w-12 h-12 flex items-center justify-center ">
                  {chart.icons}
                </div>
                <div className="px-4 flex-1">
                  <p
                    className="font-semibold font-calibri text-sm sm:text-base lg:text-lg leading-tight capitalize"
                    style={{ color: THEME_COLORS.green }}
                  >
                    {chart.title}
                  </p>
                </div>
              </div>
              <div className="flex justify-center pb-4">
                <SimplePieChart
                  data={chart.data}
                  labels={chart.labels}
                  colors={chart.colors}
                  width={300}
                  showValueOnly={true}
                  isDonut={true}
                  donutWidth="60%"
                  shows={true}
                  legendPosition="right"
                  options={{
                    dataLabels: {
                      enabled: false,
                    },
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Bar Chart and Table */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
          <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md pt-5 bg-white">
            <div className="flex items-center px-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center ">
                <ModeOFDashboardIcon className="w-12 h-12" />
              </div>
              <div className="px-4 flex-1">
                <p
                  className="text-sm sm:text-base font-semibold leading-tight"
                  style={{ color: THEME_COLORS.green }}
                >
                  Mode Of Transportation For Upstream And Downstream
                </p>
              </div>
            </div>
            <VerticalBarChart
              categories={transportModeCategories}
              data={transportModeData}
              legend="Emission from Purchased Electricity"
              colors={[
                THEME_COLORS.green,
                THEME_COLORS.yellow,
                THEME_COLORS.darkblue,
                THEME_COLORS.pink,
              ]}
              showColorCodes={true}
              xheading="Mode of Transportation"
              yheading="Percentage of Distance Travelled"
            />
          </div>
          <div className="border border-[#3BA2B959] rounded-lg shadow-md bg-white h-full flex flex-col">
            <Tier1SupplierTable
              getComponent={getComponent}
              activeDashboard={activeDashboard}
            />
          </div>
        </div>

        {/* Progress Charts Row 1 */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
          <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md bg-white pl-3 pr-3 pt-3">
            <ProgressChartSupply
              icon={<DashboardIcon401 className="w-12 h-12" />}
              primaryLegendLabel="Sustainable procurement"
              secondaryLegendLabel="Total Supplier"
              mainLabel="Sustainable procurement"
              title="Percentage of Sustainable procurement"
              totalValue={sustainablePctTotal}
              mainValue={sustainablePctMain}
              chartTotalValue={100}
              chartMainValue={sustainablePctMain}
              valueSuffix="%"
              legendPosition="right"
              footertitle="Sustainable procurement"
              status_card={true}
            />
          </div>
          <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md bg-white pl-3 pr-3 pt-3">
            <ProgressChartSupply
              icon={<DashboardIcon501 className="w-12 h-12" />}
              primaryLegendLabel="MSME procurement"
              secondaryLegendLabel="Total Supplier"
              title="Procurement through MSME suppliers"
              totalValue={msmeProcurementTotal}
              mainValue={msmeProcurementMain}
              legendPosition="right"
              footertitle="MSME suppliers"
              status_card={true}
            />
          </div>
        </div>

        {/* Progress Charts Row 2 */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
          <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md bg-white pl-3 pr-3 pt-3">
            <ProgressChartSupply
              icon={<DashboardIcon601 className="w-12 h-12" />}
              primaryLegendLabel="Female owned suppliers"
              secondaryLegendLabel="Total Supplier"
              title="Female and Male owned suppliers"
              totalValue={femaleMaleTotal}
              mainValue={femaleMaleMain}
              legendPosition="right"
              footertitle="Female owned suppliers"
              status_card={true}
            />
          </div>
          <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md bg-white">
            <SupplierCalEmission
              getComponent={getComponent}
              activeDashboard={activeDashboard}
            />
          </div>
        </div>

        {/* Emission Reduction */}
        <div className="w-full border border-[#3BA2B959] rounded-lg shadow-md mt-4 bg-white">
          <ReductionInFootpintEmission
            getComponent={getComponent}
            activeDashboard={activeDashboard}
          />
        </div>
      </div>
    </div>
  );
};

export default SupplyChain;
